import { Icon, Text, mq } from "@certn/cascadia-react";
import { formatDate } from "@certn/utils";
import { css, cx } from "@emotion/css";
import { Dispatch, KeyboardEvent, SetStateAction, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

import {
  checkResultHasUiElementsOrMessages,
  hasExternalFulfillmentNotes,
  shouldShowCheckResultExpandIcon,
  shouldShowDataWhenInProgress,
} from "./utils";
import { CheckScoreTag } from "../check-score-tag/CheckScoreTag";
import {
  CheckResult,
  FulfillmentNotes as FulfillmentNotesSchema,
} from "../types";
interface HeaderProps {
  checkResult: CheckResult;
  isPDF?: boolean;
  openResult?: boolean;
  setOpenResult: Dispatch<SetStateAction<boolean | undefined>>;
  isChildCheck?: boolean;
  fulfillmentNotes?: FulfillmentNotesSchema;
}

export function Header(props: HeaderProps) {
  const {
    isPDF = false,
    checkResult,
    setOpenResult,
    openResult,
    isChildCheck,
    fulfillmentNotes,
  } = props;
  const { t } = useTranslation();
  const statusId = useId();
  const isSpacePressedRef = useRef(false);

  if (!props) {
    return null;
  }

  const {
    type_display,
    status,
    status_display,
    score,
    sub_score,
    completed_at,
    heading_messages,
  } = checkResult;

  const isUmbrellaParent = !!(
    checkResult.child_checks && checkResult.child_checks.length > 0
  );
  const hasResults = checkResultHasUiElementsOrMessages(checkResult);
  const canShowInProgress = shouldShowDataWhenInProgress(checkResult.type);
  const isExpandable = shouldShowCheckResultExpandIcon({
    status,
    hasResults,
    canShowInProgress,
    hasFulfillmentNotes: hasExternalFulfillmentNotes(fulfillmentNotes),
    isPDF,
    isUmbrellaParent,
  });

  const toggleExpanded = () => {
    setOpenResult(!openResult);
  };

  // Matches native buttons: Enter toggles on keydown, Space toggles on keyup
  // only when the Space press started on this header, and holding either key
  // toggles only once.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    event.preventDefault();
    if (event.repeat) {
      return;
    }
    if (event.key === "Enter") {
      toggleExpanded();
    } else {
      isSpacePressedRef.current = true;
    }
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== " ") {
      return;
    }
    event.preventDefault();
    if (isSpacePressedRef.current) {
      isSpacePressedRef.current = false;
      toggleExpanded();
    }
  };

  const handleBlur = () => {
    isSpacePressedRef.current = false;
  };

  const expandableProps = isExpandable
    ? {
        role: "button",
        tabIndex: 0,
        "aria-expanded": Boolean(openResult),
        "aria-label": openResult
          ? t("Collapse {{checkName}}", { checkName: type_display })
          : t("Expand {{checkName}}", { checkName: type_display }),
        "aria-describedby": statusId,
        "data-testid": isChildCheck
          ? "check-result-card-expand-child"
          : "check-result-card-expand",
        onClick: toggleExpanded,
        onKeyDown: handleKeyDown,
        onKeyUp: handleKeyUp,
        onBlur: handleBlur,
      }
    : {};

  return (
    <div
      className={cx(
        styles.container,
        isExpandable && styles.expandable,
        isExpandable && openResult && styles.expandableOpen
      )}
      {...expandableProps}
    >
      <div className={styles.header}>
        <Text style="global/heading-large" testID="check-title">
          {type_display}
        </Text>
        {isExpandable && (
          <span aria-hidden="true" className={styles.chevron}>
            <Icon
              name={openResult ? "expand_less" : "expand_more"}
              testID="check-result-card-chevron"
            />
          </span>
        )}
        {isPDF && <CheckScoreTag score={score} subScore={sub_score} />}
      </div>
      <div id={statusId} className={styles.checkResult}>
        {!isPDF && <CheckScoreTag score={score} subScore={sub_score} />}
        {completed_at ? (
          <div aria-label={t("Check completed date")}>
            <Text style="form/body-secondary">
              {t("Completed {{date}}", {
                date: formatDate(completed_at, "PP"),
              })}
            </Text>
          </div>
        ) : (
          <div aria-label={t("Check status")} className={styles.status}>
            <Text style="form/body-secondary">{status_display}</Text>
          </div>
        )}
        {heading_messages?.map((message, index) => (
          <div key={`${message}-${index}`} className={styles.headingMessage}>
            <span className={styles.dot}>&middot;</span>
            <Text style="form/body-secondary">{message}</Text>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: css(
    mq({
      padding: "var(--space-4)",
      "@media print": { paddingLeft: 0, paddingRight: 0 },
    })
  ),
  expandable: css({
    cursor: "pointer",
    outline: "none",
    borderRadius: "var(--border-radius-3)",
    "&:hover": { background: "var(--background-ghost-hover)" },
    "&:focus-visible": {
      boxShadow: "inset 0 0 0 2px var(--border-input-active)",
    },
  }),
  expandableOpen: css({
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  }),
  header: css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "var(--space-2)",
  }),
  chevron: css({
    display: "flex",
    flexShrink: 0,
    paddingTop: "var(--space-1)",
  }),
  status: css({ display: "flex", gap: "var(--space-2)" }),
  dot: css({ lineHeight: "100%" }),
  checkResult: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: "var(--space-2)",
    rowGap: "var(--space-1)",
  }),
  headingMessage: css({
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
  }),
};
